import { useCallback, useContext, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import PlacesAutocomplete from "react-places-autocomplete";
import "./AddressAutocomplete.scss";
import waitForGoogleApi, { GOOGLE_API_LOAD_STATUS } from "app/utils/waitForGoogleApi";
import InputFormik from "app/pages/.shared/form/InputFormik";
import HorizontalDotLoader from "app/pages/.shared/HorizontalDotLoader";
import { Field } from "formik";
import useGeolocation from "app/pages/.shared/useGeolocation";
import AppGlobalsContext from "app/AppGlobalsContext";
import { inputSanitize } from "app/utils/sanitize";

const AddressAutocompleteFormik = ({
	id,
	label,
	name,
	isRequired = false,
	onError,
	onPlaceSelect,
	onBeforePlaceSelect,
	setFieldValue,
	setFieldError,
	blocPrefixName = "",
	...restProps
}) => {
	const [enableGeolocation, toggleGeolocation] = useState(false);
	const [hasChanged, setHasChanged] = useState(false);
	const [isFocused, setIsFocused] = useState(false);

	const { latitude, longitude } = useGeolocation({ enable: enableGeolocation });

	const { shop = "", publicPath = "" } = useContext(AppGlobalsContext);

	const serializeAddressComponents = place => {
		const components = place.address_components;
		let data = {
			components: {},
			formattedAddress: "",
			name: "",
		};

		const streetNumber = components.find(component => component.types[0] === "street_number");
		const route = components.find(component => component.types[0] === "route");
		const city = components.find(
			component => component.types[0] === "locality" || component.types[0] === "postal_town"
		);
		const country = components.find(component => component.types[0] === "country");
		const postalCode = components.find(
			component => component.types[0] && component.types[0].includes("postal_code")
		);
		const state = components.find(
			component => component.types[0] === "administrative_area_level_1"
		);

		data.components.streetNumber = streetNumber && streetNumber.long_name;
		data.components.route = route && route.long_name;
		data.components.city = city && city.long_name;
		data.components.country = country && country.long_name;
		data.components.postalCode = postalCode && postalCode.long_name;
		data.components.state = state && state.short_name;
		data.formattedAddress = place.formatted_address;

		if (data.components.route) {
			data.name = data.components.route;

			if (data.components.streetNumber) {
				data.name = `${data.components.streetNumber} ${data.components.route}`;
			}
		} else {
			data.name = place.name;
		}

		return data;
	};

	const onChange = address => {
		const addressLine1Name = blocPrefixName
			? `${blocPrefixName.toLowerCase()}.address-line1`
			: "address-line1";
		const addressLine2Name = blocPrefixName
			? `${blocPrefixName.toLowerCase()}.address-line2`
			: "address-line2";
		const sanitizedAddress = inputSanitize(address);
		setFieldValue(addressLine1Name, sanitizedAddress);
		setFieldValue(addressLine2Name, "");
		if (address !== "" && !hasChanged) {
			setFieldError(addressLine1Name);
			setFieldError(addressLine2Name);
			setHasChanged(true);
		}
	};

	const handleFocus = useCallback(() => {
		setIsFocused(true);
		if (!enableGeolocation) {
			toggleGeolocation(true);
		}
	}, [enableGeolocation]);

	const onSelect = (address, placeId) => {
		// Google peut déclencher onSelect sans placeId (ex : Entrée sur une saisie manuelle),
		// on ignore alors pour éviter de lancer une recherche de détails inutile et bloquer les loaders.
		if (!placeId) {
			return;
		}
		// on valorise le champ avec le numero de rue et le nom de la rue avant l'arrivée du détail de l'adresse
		const rue = address?.split(",")[0];
		onChange(rue);

		if (typeof onBeforePlaceSelect === "function") {
			onBeforePlaceSelect();
		}
		// eslint-disable-next-line no-undef
		const service = new google.maps.places.PlacesService(document.createElement("div"));
		service.getDetails({ placeId }, (place, status) => {
			// eslint-disable-next-line no-undef
			if (status === google.maps.places.PlacesServiceStatus.OK) {
				const placeData = serializeAddressComponents(place);
				if (typeof onPlaceSelect === "function") {
					onPlaceSelect(placeData);
				} else {
					onChange(placeData.formattedAddress);
				}
			} else if (typeof onError === "function") {
				onError("getDetails", status);
			}
		});
	};

	const getInputClassname = (field, meta, googleAPILoadingStatus) => {
		return classNames("address-autocomplete control-group", {
			"control-group--touched": meta.touched || field.value,
			"control-group--error": meta.touched && meta.error,
			"control-group--disabled": googleAPILoadingStatus === GOOGLE_API_LOAD_STATUS.LOADING,
		});
	};

	const handleBlur = () => {
		setHasChanged(false);
		setIsFocused(false);
	};
	let options = {
		types: ["address"],
	};

	const [, country] = shop.split("-");

	if (country) {
		options.componentRestrictions = {
			country,
		};
	}

	const googleAPILoadingStatus = waitForGoogleApi();

	if (googleAPILoadingStatus === GOOGLE_API_LOAD_STATUS.READY && latitude && longitude) {
		// eslint-disable-next-line no-undef
		options.location = new google.maps.LatLng(latitude, longitude);
		options.radius = 1500000;
		options.strictbounds = true;
	}

	// should use `useField()` instead of <Field /> when migrated to functionalComponent
	return (
		<Field name={name}>
			{({ field, meta }) => {
				const shouldFetchSuggestions = Boolean(
					isFocused && field.value && field.value.length > 6
				);

				if (googleAPILoadingStatus === GOOGLE_API_LOAD_STATUS.LOADING) {
					return (
						<div className={getInputClassname(field, meta, googleAPILoadingStatus)}>
							<div style={{ position: "relative" }}>
								<InputFormik
									id={id}
									name={name}
									isRequired={isRequired}
									disabled
									label={label}
									maxLength={50}
								/>
								<div className="address-autocomplete__loader">
									<HorizontalDotLoader dotSize="3px" color="#3493dc" />
								</div>
							</div>
						</div>
					);
				} else if (googleAPILoadingStatus === GOOGLE_API_LOAD_STATUS.ERROR) {
					return (
						<div className={getInputClassname(field, meta, googleAPILoadingStatus)}>
							<InputFormik
								id={id}
								name={name}
								isRequired={isRequired}
								label={label}
								data-testid={restProps["data-testid"]}
								maxLength={50}
								onBlur={handleBlur}
							/>
						</div>
					);
				} else if (googleAPILoadingStatus === GOOGLE_API_LOAD_STATUS.READY) {
					return (
						<div className={getInputClassname(field, meta, googleAPILoadingStatus)}>
							<PlacesAutocomplete
								value={field.value}
								onChange={onChange}
								onSelect={onSelect}
								searchOptions={options}
								shouldFetchSuggestions={shouldFetchSuggestions}
								highlightFirstSuggestion={true}
							>
								{({
									getInputProps,
									suggestions = [],
									getSuggestionItemProps,
									loading,
								}) => {
									return (
										<div style={{ position: "relative" }}>
											<InputFormik
												id={id}
												name={name}
												isRequired={isRequired}
												label={label}
												{...getInputProps()}
												shouldSanitize={false}
												autoComplete="disabled"
												onFocus={handleFocus}
												data-testid={restProps["data-testid"]}
												onBlur={handleBlur}
											/>

											{loading && (
												<div className="address-autocomplete__loader">
													<HorizontalDotLoader
														dotSize="3px"
														color="#3493dc"
													/>
												</div>
											)}

											{isFocused && suggestions.length > 0 && (
												<div className="address-autocomplete__container">
													{suggestions.map(suggestion => {
														return (
															<div
																key={suggestion.description}
																{...getSuggestionItemProps(
																	suggestion
																)}
																className={classNames(
																	{
																		"address-autocomplete__item--active":
																			suggestion.active,
																	},
																	"address-autocomplete__item"
																)}
															>
																<span>
																	{suggestion.description}
																</span>
															</div>
														);
													})}
													<div className="address-autocomplete__dropdown-footer">
														<img
															src={`${publicPath}/icons/powered_by_google.png`}
															alt="powered by google"
														/>
													</div>
												</div>
											)}
										</div>
									);
								}}
							</PlacesAutocomplete>
						</div>
					);
				}

				return (
					<div className={getInputClassname(field, meta, googleAPILoadingStatus)}>
						<InputFormik
							id={id}
							name={name}
							isRequired={isRequired}
							label={label}
							maxLength={50}
							data-testid={restProps["data-testid"]}
							onBlur={handleBlur}
						/>
					</div>
				);
			}}
		</Field>
	);
};

AddressAutocompleteFormik.defaultProps = {
	shop: "",
	isRequired: false,
	coords: {},
};

AddressAutocompleteFormik.propTypes = {
	id: PropTypes.string,
	shop: PropTypes.string,
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	onPlaceSelect: PropTypes.func,
	onBeforePlaceSelect: PropTypes.func,
	onError: PropTypes.func,
	name: PropTypes.string,
	isRequired: PropTypes.bool,
	setFieldValue: PropTypes.func.isRequired,
	setFieldError: PropTypes.func.isRequired,
	blocPrefixName: PropTypes.string,
};

export default AddressAutocompleteFormik;
